/**
 * 顾客。
 *
 * 默认就显示列表（按最近有动静排序），不是空白等你搜。
 * 第一版只做搜索，结果顾客一多，老板连「这个人在不在系统里」都不知道 ——
 * 搜索优先是对的，但不该以牺牲「能看到有哪些人」为代价。
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { History, Search, UserPlus, Users, X } from 'lucide-react'
import { api, type Me, type SearchItem } from '../lib/api'
import { Avatar, Badge, Button, Card, EmptyState, Input, Skeleton, cx } from '../components/ui'
import { date as fmtDate, relativeDay, rm } from '../lib/format'
import { NewCustomerDrawer } from '../features/NewCustomerDrawer'
import { BackfillDrawer } from '../features/BackfillDrawer'

const PAGE = 30

export function CustomersPage({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const [limit, setLimit] = useState(PAGE)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])

  // 换关键词时把「加载更多」的页数重置，否则搜索结果会莫名很长
  useEffect(() => {
    setLimit(PAGE)
  }, [debounced])

  const searching = debounced.trim().length >= 2

  const results = useQuery({
    queryKey: ['customer-list', debounced, limit],
    queryFn: () =>
      api.get<{ items: SearchItem[]; total: number | null }>(
        `/api/customers/search?q=${encodeURIComponent(debounced)}&limit=${limit}`,
      ),
    placeholderData: (p) => p,
  })

  const items = results.data?.items ?? []
  const [backfillOpen, setBackfillOpen] = useState(false)
  const total = results.data?.total ?? null
  const hasMore = !searching && total !== null && items.length < total

  return (
    <div className="mx-auto max-w-[1120px]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-[28px] font-bold">
          顾客
          {total !== null && (
            <span className="tnum ml-2 text-[19px] font-normal text-ink-3">共 {total} 位</span>
          )}
        </h1>
        <div className="flex gap-2">
          {/* 补录入口放在顾客页而不是设置里 —— 老板照着账本抄的时候，
              他人就在这一页 */}
          <Button icon={<History size={20} />} onClick={() => setBackfillOpen(true)}>
            补录老顾客
          </Button>
          <Button variant="primary" icon={<UserPlus size={20} />} onClick={() => setNewOpen(true)}>
            新顾客
          </Button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={20}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3"
        />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="电话后 4 位 / 姓名 / 顾客编号"
          className="px-11"
        />
        {q && (
          <button
            onClick={() => setQ('')}
            aria-label="清空搜索"
            className="absolute right-1.5 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-ink-3 hover:bg-sunken"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {results.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-18" />
          <Skeleton className="h-18" />
          <Skeleton className="h-18" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={44} />}
            title={searching ? `找不到「${debounced}」相关的顾客` : '还没有顾客'}
            description={searching ? undefined : '建了第一位顾客之后，这里会按最近来店排序。'}
            action={
              <Button
                variant="primary"
                size="lg"
                icon={<UserPlus size={20} />}
                onClick={() => setNewOpen(true)}
              >
                {searching ? '用这个新建顾客' : '新建第一位顾客'}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <p className="mb-2 text-[15px] text-ink-3">
            {searching ? `找到 ${items.length} 位` : '按最近来店排序'}
          </p>

          <Card bodyClassName="p-0! sm:p-0!" className={cx(results.isFetching && 'opacity-60')}>
            {/* 平板起用表格，手机降级为卡片列表 */}
            <table className="hidden w-full md:table">
              <thead>
                <tr className="border-b border-line bg-sunken text-left text-[13px] font-semibold text-ink-2">
                  <th className="px-5 py-3">姓名</th>
                  <th className="px-5 py-3">电话</th>
                  <th className="px-5 py-3">上次到店</th>
                  <th className="px-5 py-3 text-right">工单</th>
                  <th className="px-5 py-3 text-right">状态</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-sunken"
                  >
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-3">
                        <Avatar name={c.fullName} size={36} />
                        <span>
                          <span className="block text-[17px] font-semibold">{c.fullName}</span>
                          <span className="tnum block text-[14px] text-ink-3">
                            {c.code}
                            {c.area ? ` · ${c.area}` : ''}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className="tnum px-5 py-3 text-[17px]">{c.phoneMasked}</td>
                    <td className="px-5 py-3 text-[17px]">
                      {c.lastVisitAt ? (
                        <>
                          <span className="tnum block">{fmtDate(c.lastVisitAt)}</span>
                          <span className="block text-[14px] text-ink-3">
                            {relativeDay(c.lastVisitAt)}
                          </span>
                        </>
                      ) : (
                        <span className="text-ink-3">还没到过店</span>
                      )}
                    </td>
                    <td className="tnum px-5 py-3 text-right text-[17px]">{c.caseCount}</td>
                    <td className="px-5 py-3 text-right">
                      {c.hasOutstanding && (
                        <Badge tone="warn">
                          {c.outstandingAmount === null ? '有欠款' : `欠 ${rm(c.outstandingAmount)}`}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="md:hidden">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="flex w-full items-center gap-3 border-b border-line px-4 py-3.5 text-left last:border-0"
                  >
                    <Avatar name={c.fullName} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[17px] font-semibold">{c.fullName}</span>
                        {c.hasOutstanding && <Badge tone="warn">欠款</Badge>}
                      </span>
                      <span className="tnum block text-[15px] text-ink-2">{c.phoneMasked}</span>
                      <span className="block text-[14px] text-ink-3">
                        {c.caseCount} 张工单
                        {c.lastVisitAt ? ` · 上次 ${relativeDay(c.lastVisitAt)}` : ' · 还没到过店'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <Button size="lg" loading={results.isFetching} onClick={() => setLimit((n) => n + PAGE)}>
                加载更多（还有 {total! - items.length} 位）
              </Button>
            </div>
          )}
        </>
      )}

      <NewCustomerDrawer
        open={newOpen}
        initialPhone={/^\d+$/.test(debounced) ? debounced : ''}
        initialName={/^\d+$/.test(debounced) ? '' : debounced}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => navigate(`/customers/${id}`)}
      />

      <BackfillDrawer open={backfillOpen} onClose={() => setBackfillOpen(false)} />
    </div>
  )
}
